import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Lock, ChevronDown, Sparkles, Star } from "lucide-react";
import Envelope from "@/components/Envelope";
import PaperTexture from "@/components/PaperTexture";

const EMOTION_LINES = [
  {
    id: "love",
    label: "For love",
    line: "You are the one I want to write to on ordinary Tuesdays, not only on grand occasions.",
  },
  {
    id: "missing",
    label: "For missing them",
    line: "Some corner of me still turns toward the door each evening, quietly hoping it is you.",
  },
  {
    id: "sorry",
    label: "For an apology",
    line: "I was wrong. I have been holding these words for weeks — and they belong to you first.",
  },
  {
    id: "gratitude",
    label: "For gratitude",
    line: "Thank you for the way you make ordinary days feel gentler. Small mercies. Big love.",
  },
  {
    id: "birthday",
    label: "For a birthday",
    line: "May this year hold every small joy you have been quietly waiting for — you deserve it.",
  },
  {
    id: "just_because",
    label: "Just because",
    line: "No reason. No occasion. Just this — you crossed my mind, and I did not want it to pass.",
  },
];

const Faq = ({ q, a }) => (
  <details className="group border-b border-[#1C1917]/10 py-5">
    <summary className="cursor-pointer flex justify-between items-center list-none">
      <span className="font-serif-elegant text-xl md:text-2xl text-[#1C1917]">{q}</span>
      <ChevronDown className="text-[#1C1917]/60 group-open:rotate-180 transition-transform" size={20} />
    </summary>
    <p className="mt-3 text-[#4c3f34] leading-relaxed">{a}</p>
  </details>
);

const MiniTime = ({ label, v }) => (
  <div className="flex flex-col items-center">
    <div className="font-serif-elegant text-3xl text-[#1C1917] tabular-nums">{v}</div>
    <div className="uppercase tracking-widest text-[9px] text-[#4c3f34]/70">{label}</div>
  </div>
);

export default function Home() {
  const [revealed, setRevealed] = useState([]); // ids in order of click

  const toggle = (id) => {
    setRevealed((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const clear = () => setRevealed([]);

  return (
    <div className="min-h-screen w-full cream-bg text-[#1C1917]">
      {/* Nav */}
      <nav className="max-w-7xl mx-auto flex items-center justify-between px-6 py-6">
        <div className="flex items-center gap-3">
          <Heart className="text-[#991B1B]" fill="#991B1B" size={22} />
          <span className="font-serif-elegant text-2xl tracking-tight">WrittenHearts</span>
        </div>
        <div className="hidden md:flex items-center gap-8 text-sm text-[#4c3f34]">
          <a href="#letter" className="hover:text-[#1C1917] transition-colors">How it works</a>
          <a href="#capsule" className="hover:text-[#1C1917] transition-colors">Time capsule</a>
          <a href="#promise" className="hover:text-[#1C1917] transition-colors">Privacy</a>
          <a href="#faq" className="hover:text-[#1C1917] transition-colors">FAQ</a>
        </div>
        <Link
          to="/compose"
          data-testid="nav-write-btn"
          className="text-sm px-5 py-2 rounded-full bg-[#1C1917] text-[#FDFBF7] hover:bg-[#3f342c] transition-colors"
        >
          Write a letter
        </Link>
      </nav>

      {/* Hero */}
      <section className="relative max-w-7xl mx-auto px-6 pt-8 md:pt-20 pb-24 grid md:grid-cols-2 gap-16 items-center">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="uppercase tracking-[0.4em] text-xs text-[#991B1B]"
          >
            A quiet corner of the internet
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.15 }}
            className="mt-4 font-serif-elegant text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-[#1C1917]"
          >
            Not a message.
            <br />
            <span className="italic text-[#7F1D1D]">A moment.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.6 }}
            className="mt-6 text-lg text-[#4c3f34] max-w-lg leading-relaxed"
          >
            Create beautiful handwritten digital letters that feel as personal as real ones — sealed with wax, delivered with care, opened like a keepsake.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="mt-10 flex items-center gap-4 flex-wrap"
          >
            <Link
              to="/compose"
              data-testid="hero-write-btn"
              className="px-8 py-4 rounded-full bg-[#991B1B] text-white hover:bg-[#7F1D1D] transition-colors shadow-lg shadow-[#991B1B]/25 text-sm tracking-wide"
            >
              Write Your Letter
            </Link>
            <a
              href="#letter"
              data-testid="hero-howitworks-btn"
              className="px-8 py-4 rounded-full border border-[#1C1917]/20 text-[#1C1917] hover:bg-[#1C1917]/5 transition-colors text-sm tracking-wide"
            >
              See How It Works
            </a>
          </motion.div>
          <div className="mt-5">
            <Link
              to="/compose?mode=capsule"
              data-testid="hero-capsule-link"
              className="inline-flex items-center gap-2 text-sm text-[#7F1D1D] hover:text-[#991B1B] italic underline underline-offset-4 decoration-[#991B1B]/30"
            >
              …or write a time capsule to your future self →
            </Link>
          </div>
          <div className="mt-10 flex items-center gap-3 text-[#4c3f34]/70 text-xs">
            <Lock size={13} />
            <span>One letter. For someone special. One unforgettable moment.</span>
          </div>
          <div className="mt-3 text-[#4c3f34]/70 text-xs">
            <span>₹19 for your first letter · ₹29 for each after · secure UPI, cards & netbanking</span>
          </div>
        </div>

        {/* Envelope illustration */}
        <motion.div
          initial={{ opacity: 0, y: 60, rotate: -6 }}
          animate={{ opacity: 1, y: 0, rotate: -3 }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          className="flex justify-center md:justify-end"
        >
          <div className="relative">
            <div className="absolute -inset-10 rounded-full bg-[#D9BE93]/40 blur-3xl" />
            <Envelope sealed recipient="Someone Dear" sender="A friend" size="lg" stamp="red_heart" pulse />
            <motion.div
              className="absolute -top-6 -right-4"
              animate={{ y: [-6, -32], opacity: [0.6, 0] }}
              transition={{ repeat: Infinity, duration: 3.2, ease: "easeOut" }}
            >
              <Heart size={22} className="text-[#D97757]" fill="#D97757" />
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* How it works — interactive digital letter with clickable emotional prompts */}
      <section id="letter" className="max-w-4xl mx-auto px-6 py-24">
        <div className="text-center">
          <p className="uppercase tracking-[0.4em] text-xs text-[#991B1B]">How it works</p>
          <h2 className="mt-3 font-serif-elegant text-4xl md:text-5xl text-[#1C1917]">
            Tap a feeling. Watch it write itself.
          </h2>
          <p className="mt-3 text-[#4c3f34]/80 max-w-xl mx-auto text-sm md:text-base">
            This letter is empty — until you press one of the words below. Each one inks a real line
            into the paper. That is exactly how writing a WrittenHearts letter feels.
          </p>
        </div>

        {/* Emotion chips */}
        <div className="mt-10 flex flex-wrap gap-3 justify-center">
          {EMOTION_LINES.map((e) => {
            const on = revealed.includes(e.id);
            return (
              <button
                key={e.id}
                data-testid={`emo-chip-${e.id}`}
                onClick={() => toggle(e.id)}
                className={`px-5 py-2.5 rounded-full text-sm border transition-all duration-300 hover:-translate-y-0.5 ${
                  on
                    ? "bg-[#991B1B] text-white border-transparent shadow-md shadow-[#991B1B]/25"
                    : "bg-[#FDFBF7] text-[#1C1917] border-[#1C1917]/15 hover:border-[#991B1B]/40"
                }`}
              >
                {on ? "✓ " : ""}
                {e.label}
              </button>
            );
          })}
        </div>

        {/* wax dot */}
        <div className="flex justify-center mt-10 mb-[-14px] relative z-10">
          <div className="wax-seal w-7 h-7 rounded-full flex items-center justify-center shadow-md shadow-[#991B1B]/40">
            <Heart size={11} className="text-[#F5D6D6]" fill="#F5D6D6" />
          </div>
        </div>

        <PaperTexture className="p-8 md:p-14 min-h-[420px]">
          <div className="font-hand text-2xl md:text-3xl text-[#7F1D1D]">Dear reader,</div>

          <div className="mt-4 space-y-5 font-serif-elegant text-lg md:text-xl text-[#1C1917] leading-relaxed min-h-[220px]">
            <AnimatePresence>
              {revealed.length === 0 && (
                <motion.p
                  key="placeholder"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="italic text-[#1C1917]/40"
                  data-testid="letter-placeholder"
                >
                  (Tap a feeling above and watch the ink appear here…)
                </motion.p>
              )}
              {revealed.map((id) => {
                const item = EMOTION_LINES.find((x) => x.id === id);
                if (!item) return null;
                return (
                  <motion.p
                    key={id}
                    initial={{ opacity: 0, y: 8, filter: "blur(3px)" }}
                    animate={{ opacity: 1, y: 0, filter: "blur(0)" }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.75, ease: "easeOut" }}
                    data-testid={`letter-line-${id}`}
                    className="relative"
                  >
                    <span className="absolute -left-4 top-2 w-1 h-1 rounded-full bg-[#991B1B]" />
                    {item.line}
                  </motion.p>
                );
              })}
            </AnimatePresence>
          </div>

          <div className="mt-10 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="font-hand text-2xl text-[#7F1D1D]">— with love, WrittenHearts</div>
            <div className="flex items-center gap-3">
              {revealed.length > 0 && (
                <button
                  data-testid="letter-clear-btn"
                  onClick={clear}
                  className="text-xs uppercase tracking-widest text-[#4c3f34] hover:text-[#1C1917]"
                >
                  clear the page
                </button>
              )}
              <Link
                to="/compose"
                data-testid="letter-begin-btn"
                className="px-6 py-2.5 rounded-full bg-[#991B1B] text-white hover:bg-[#7F1D1D] transition-colors text-sm inline-flex items-center gap-2"
              >
                <Sparkles size={14} /> Begin your own letter
              </Link>
            </div>
          </div>
        </PaperTexture>

        <p className="text-center text-xs text-[#4c3f34]/60 mt-6 italic">
          A hint: the platform is full of small surprises. Some show only when you write.
        </p>
      </section>

      {/* Time Capsule feature */}
      <section id="capsule" className="max-w-6xl mx-auto px-6 py-24">
        <div className="grid md:grid-cols-2 gap-14 items-center">
          <div>
            <p className="uppercase tracking-[0.4em] text-xs text-[#991B1B] flex items-center gap-2">
              <span>Time capsule letters</span>
            </p>
            <h2 className="mt-3 font-serif-elegant text-4xl md:text-5xl text-[#1C1917] leading-[1.05]">
              A letter to your <span className="italic text-[#7F1D1D]">future self.</span>
            </h2>
            <p className="mt-4 text-[#4c3f34] leading-relaxed max-w-lg">
              Write today. Choose a date — one month away, one year, five years.
              The letter locks. A countdown waits. When the moment arrives, you tap the wax and read a message
              from a version of you that no longer exists.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-[#4c3f34]">
              {[
                "Sealed. Cannot be opened even a second early.",
                "Countdown shows the years, months, days remaining.",
                "Written on / opened on dates appear at the top when it unlocks.",
                "Photos, voice notes, and stamps travel with it.",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Heart size={12} className="text-[#991B1B] mt-1.5" fill="#991B1B" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/compose?mode=capsule"
              data-testid="capsule-cta-btn"
              className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1C1917] text-[#FDFBF7] hover:bg-[#3f342c] transition-colors text-sm"
            >
              <Sparkles size={14} /> Write to your future self
            </Link>
          </div>

          {/* preview envelope with future date badge */}
          <div className="flex justify-center md:justify-end">
            <div className="relative">
              <div className="absolute -inset-10 rounded-full bg-[#D9BE93]/40 blur-3xl" />
              <div className="relative rounded-[6px] bg-[#FDFBF7] border border-[#1C1917]/8 shadow-[0_30px_60px_-30px_rgba(120,80,40,0.35)] p-6">
                <div className="flex items-center gap-2 text-[#991B1B] uppercase tracking-widest text-[10px]">
                  <span>Sealed until</span>
                </div>
                <div className="mt-1 font-serif-elegant text-2xl text-[#1C1917]">
                  27 July 2027
                </div>
                <div className="mt-4 flex gap-4 items-end">
                  <MiniTime label="years" v="1" />
                  <MiniTime label="months" v="0" />
                  <MiniTime label="days" v="14" />
                  <MiniTime label="hh" v="06" />
                  <MiniTime label="mm" v="22" />
                </div>
                <div className="mt-6 flex items-center gap-2 text-xs text-[#4c3f34]">
                  <span className="w-2 h-2 rounded-full bg-[#991B1B] animate-pulse" />
                  Cannot be opened before this moment.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy promise */}
      <section id="promise" className="max-w-4xl mx-auto px-6 py-20">
        <div className="rounded-[4px] bg-[#FDFBF7] border border-[#1C1917]/8 p-10 md:p-16 relative overflow-hidden shadow-[0_30px_60px_-30px_rgba(120,80,40,0.25)]">
          <p className="uppercase tracking-[0.4em] text-xs text-[#991B1B]">Our promise</p>
          <h2 className="mt-3 font-serif-elegant text-4xl md:text-5xl">A letter for one heart.</h2>
          <div className="mt-8 grid sm:grid-cols-2 gap-6 text-[#3f342c]">
            {[
              "No public profiles, no feeds, no likes.",
              "Only someone special with your link can open it.",
              "We never read your letters or use them for ads or AI.",
              "The admin dashboard only sees anonymous analytics.",
            ].map((t, i) => (
              <div key={i} className="flex items-start gap-3">
                <Heart size={16} className="text-[#991B1B] mt-1" fill="#991B1B" />
                <p className="text-sm leading-relaxed">{t}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 font-hand text-3xl text-[#991B1B]">— One letter. For someone special. One moment.</p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-6 py-24">
        <p className="uppercase tracking-[0.4em] text-xs text-[#991B1B]">Questions</p>
        <h2 className="mt-3 font-serif-elegant text-4xl md:text-5xl text-[#1C1917]">Small answers, big feelings.</h2>
        <div className="mt-10">
          <Faq q="Do I need an account to write a letter?" a="No account needed. Just write, choose a stamp, seal, and send. Your letter lives at a unique, unguessable link." />
          <Faq q="Can I add photos or a voice message?" a="Yes. Tuck in printed-style photos, and record a short voice note that plays inside the letter." />
          <Faq q="Is it truly private?" a="Every letter is written for someone special only. There are no feeds, no followers, no comments. The link is signed and hard to guess." />
          <Faq q="How long does the letter stay?" a="Forever, unless someone special chooses to save or delete it. Nothing here disappears in 24 hours." />
        </div>
      </section>

      {/* Footer */}
      <footer className="max-w-7xl mx-auto px-6 py-14 border-t border-[#1C1917]/10 flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-2 text-[#4c3f34]">
          <Heart size={16} className="text-[#991B1B]" fill="#991B1B" />
          <span className="font-serif-elegant text-lg">WrittenHearts</span>
          <span className="text-xs text-[#4c3f34]/60">— Not a message. A moment.</span>
        </div>
        <div className="flex items-center gap-6 text-xs text-[#4c3f34]/70">
          <a href="#promise" className="hover:text-[#1C1917]">Privacy</a>
          <a href="#faq" className="hover:text-[#1C1917]">FAQ</a>
          <Link to="/admin" data-testid="footer-admin-link" className="hover:text-[#1C1917]">Admin</Link>
        </div>
      </footer>
    </div>
  );
}
